import React, { useContext, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Tooltip,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import FooterActionButton from "./FooterActionButton";
import UnsavedChangesDialog from "./UnsavedChangesDialog";
import { useBrandAppearance } from "../hooks/useBrandAppearance";
import { clearToken } from "../services/AuthService";
import { getLovForField, guestsReadyToProceed } from "../utils/guestUtils";
import { getTenantConfig } from "../config/tenant";

interface FooterProps {
    currentStep: number;
    steps: string[];
    onProceed: () => void;
    onSave: () => void;
    isSaving: boolean;
}

const Footer: React.FC<FooterProps> = ({
    currentStep,
    steps,
    onProceed,
    onSave,
    isSaving,
}) => {
    const { colors: BrandColors } = useBrandAppearance();
    const { state, setIsFormDirty } = useContext(AppContext) ?? {};
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
    // Resolve the per-field LOV map once for the gate. Each call to getLovForField applies the
    // chain's lovSources config (defaults preserve pre-V14 hard-wiring) and the empty-LOV
    // fallback. Without this, a chain that flips `addressCountry: NATIONALITY` would still see
    // its addresses validated against the COUNTRYCODES map and Proceed would never enable.
    const lovMaps = { nationalities: state?.nationalities, countries: state?.countries };
    const tenantLovSources = getTenantConfig()?.lovSources;
    const mandatoryDataLovs = {
        nationality: getLovForField("nationality", tenantLovSources, lovMaps),
        birthCountry: getLovForField("birthCountry", tenantLovSources, lovMaps),
        addressCountry: getLovForField("addressCountry", tenantLovSources, lovMaps),
    };
    const isProceedDisabled = !guestsReadyToProceed(state?.guests ?? [], mandatoryDataLovs);

    const navigate = useNavigate();
    const { t } = useTranslation("footer");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const routeLocation = useLocation();

    const isGuestDetailsPage = routeLocation.pathname.includes("/guest-details");
    const isCheckInCompletePage = routeLocation.pathname.includes("/check-in-complete");

    const doLogout = () => {
        clearToken();
        sessionStorage.removeItem("lastName");
        sessionStorage.removeItem("confirmationNumber");
        setIsFormDirty?.(false);
        navigate("/");
    };

    const handleLogout = () => {
        if (state?.isFormDirty) {
            setShowSignOutConfirm(true);
            return;
        }
        doLogout();
    };

    const confirmUrl = getTenantConfig()?.branding?.confirmUrl || "/";
    const handleConfirm = () => {
        window.location.href = confirmUrl;
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "sticky",
                bottom: 0,
                width: "100%",
                height: isMobile ? "80px" : "110px",
                backgroundColor: "var(--oc-footer-bg)",
                padding: 0,
                boxShadow: isMobile ? undefined : "0px 0px 10px rgba(0,0,0,0.1)",
            }}
        >
            {/* Left: Sign Out */}
            <Button
                variant="contained"
                startIcon={<ExitToAppIcon />}
                onClick={handleLogout}
                sx={{
                    backgroundColor: BrandColors.primary,
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    borderRadius: 0,
                    width: isMobile ? "30%" : { xs: "100%", md: "190px" },
                    height: "100%",
                    minWidth: 0,
                    fontSize: isMobile ? "0.75rem" : undefined,
                    "&:hover": { backgroundColor: BrandColors.primary },
                }}
            >
                {t("signOutButton")}
            </Button>

            {/* Center: Steps (hidden on check-in-complete) */}
            {isMobile ? (
                !isCheckInCompletePage ? (
                    <Box
                        sx={{
                            width: "33.33%",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 1,
                        }}
                    >
                        <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: "bold", color: "#000" }}>
                            STEP
                        </Typography>

                        <Box sx={{ position: "relative", display: "inline-block", width: "100%" }}>
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: 0,
                                    right: 0,
                                    height: "1px",
                                    backgroundColor: BrandColors.accent,
                                    zIndex: 0,
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "#7C624B",
                                    fontSize: "0.85rem",
                                    fontWeight: "bold",
                                    position: "relative",
                                    zIndex: 1,
                                    px: 1,
                                    backgroundColor: "var(--oc-footer-bg)",
                                }}
                            >
                                {currentStep} / {steps.length}
                            </Typography>
                        </Box>

                        <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: "bold", color: "#000", mt: 0.3 }}>
                            {t(steps[currentStep - 1])}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ flex: 1 }} />
                )
            ) : (
                !isGuestDetailsPage &&
                !isCheckInCompletePage && (
                    <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "center", alignItems: "center" }}>
                        {steps.map((stepKey, index) => (
                            <Box
                                key={index}
                                sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}
                            >
                                {index !== 0 && (
                                    <Box
                                        sx={{
                                            width: 120,
                                            height: "2px",
                                            backgroundColor: "#C4B59D",
                                            marginRight: "3px",
                                            marginBottom: "25px",
                                        }}
                                    />
                                )}
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            backgroundColor:
                                                currentStep === index + 1 ? BrandColors.currentStep : BrandColors.nextStep,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#FFFFFF",
                                            fontWeight: "bold",
                                            mb: 1,
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#000", textAlign: "center" }}>
                                        {t(stepKey)}
                                    </Typography>
                                </Box>
                                {index !== steps.length - 1 && (
                                    <Box
                                        sx={{
                                            width: 120,
                                            height: "2px",
                                            backgroundColor: "#C4B59D",
                                            marginLeft: "3px",
                                            marginBottom: "25px",
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                )
            )}

            {/* Right: Action area */}
            {!isMobile ? (
                isCheckInCompletePage ? (
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        sx={{
                            backgroundColor: BrandColors.nextStep,
                            color: "#FFFFFF",
                            fontWeight: "bold",
                            borderRadius: 0,
                            width: { xs: "100%", md: "190px" },
                            height: "110px",
                            minWidth: 0,
                            "&:hover": { backgroundColor: BrandColors.nextStep },
                        }}
                    >
                        {t("confirmButton", { defaultValue: "CONFIRM" })}
                    </Button>
                ) : (
                    <Tooltip
                        title={
                            isGuestDetailsPage && !state.isFormValid ? (
                                <Typography
                                    sx={{
                                        fontSize: ".9rem",
                                        fontWeight: 400,
                                        color: "#FFFFFF",
                                        maxWidth: "250px",
                                        textAlign: "left",
                                        lineHeight: "1.5",
                                        padding: "5px",
                                    }}
                                >
                                    {t("saveButtonTooltip")}
                                </Typography>
                            ) : !isGuestDetailsPage && isProceedDisabled ? (
                                <Typography
                                    sx={{
                                        fontSize: ".9rem",
                                        fontWeight: 400,
                                        color: "#FFFFFF",
                                        maxWidth: "250px",
                                        textAlign: "left",
                                        lineHeight: "1.5",
                                        padding: "5px",
                                    }}
                                >
                                    {t("proceedButtonTooltip")}
                                </Typography>
                            ) : (
                                ""
                            )
                        }
                        open={
                            (isGuestDetailsPage && !state.isFormValid && !isSaving) ||
                                (!isGuestDetailsPage && isProceedDisabled && !isSaving)
                                ? tooltipOpen
                                : false
                        }
                        onOpen={() => setTooltipOpen(true)}
                        onClose={() => setTooltipOpen(false)}
                        disableHoverListener={
                            isGuestDetailsPage ? state.isFormValid || isSaving : !isProceedDisabled || isSaving
                        }
                        arrow
                        placement="top"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: "#000000",
                                    borderRadius: "4px",
                                    padding: "12px",
                                    boxShadow: "0px 4px 12px rgba(0,0,0,0.3)",
                                    maxWidth: "300px",
                                    marginRight: "10px",
                                },
                            },
                            arrow: { sx: { color: "#000000" } },
                        }}
                    >
                        <span>
                            <FooterActionButton
                                isGuestDetailsPage={isGuestDetailsPage}
                                isFormValid={state.isFormValid}
                                isProceedDisabled={isProceedDisabled}
                                onProceed={onProceed}
                                onSave={onSave}
                                isMobile={isMobile}
                                isSaving={isSaving}
                            />
                        </span>
                    </Tooltip>
                )
            ) : isCheckInCompletePage ? (
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    sx={{
                        backgroundColor: BrandColors.nextStep,
                        color: "#FFFFFF",
                        fontWeight: "bold",
                        borderRadius: 0,
                        width: "30%",
                        height: "80px",
                        minWidth: 0,
                        fontSize: "0.75rem",
                        "&:hover": { backgroundColor: BrandColors.nextStep },
                    }}
                >
                    {t("confirmButton", { defaultValue: "CONFIRM" })}
                </Button>
            ) : (
                <FooterActionButton
                    isGuestDetailsPage={isGuestDetailsPage}
                    isFormValid={state.isFormValid}
                    isProceedDisabled={isProceedDisabled}
                    onProceed={onProceed}
                    onSave={onSave}
                    isMobile={isMobile}
                    isSaving={isSaving}
                />
            )}

            <UnsavedChangesDialog
                open={showSignOutConfirm}
                onStay={() => setShowSignOutConfirm(false)}
                onLeave={() => {
                    setShowSignOutConfirm(false);
                    doLogout();
                }}
            />
        </Box>
    );
};

export default Footer;
